import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Loader2, AlertCircle, CheckCircle, FileText, Save, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ConfirmationCard, OnboardingCard, OnboardingMilestones, TagEditor } from './onboarding/OnboardingPrimitives';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import {
  fetchCanonicalCv,
  saveCanonicalCv,
  confirmCvAndStartSearch,
  updateDraftPreferences,
} from '../data/mockJobResults';
import { getSafeErrorMessage, toSafeDisplayString } from '../lib/apiErrors';
import { splitList, uniqueArray } from '../data/candidateIntake';

// -- Section schema ----------------------------------------------------
//
// Each section maps to a key on canonical_cv_json. We deliberately keep
// each section as a single editable string so it's easy for users to read
// and correct details (especially Education, which extraction often misses).

// Section keys mirror canonical_cv_json (schema_version 'canonical-cv-v1').
// Most sections are arrays of strings on the wire; we render them as one
// line per item in a textarea, then split back into arrays on save.
// `professional_summary` stays a single string.
const SECTIONS = [
  { key: 'professional_summary', label: 'Professional Summary',
    helper: 'A short overview from the CV - only filled if the CV has a clear Summary / Profile / About section.',
    rows: 4 },
  { key: 'skills', label: 'Skills',
    helper: 'These are the skills shown in your CV. They should reflect what is actually supported by your CV content (tools, technologies, methodologies - one per line or comma-separated).', rows: 4 },
  { key: 'projects', label: 'Projects',
    helper: 'Side projects, open-source, notable work (one per line).', rows: 4 },
  { key: 'certifications', label: 'Certifications',
    helper: 'Courses, certifications, awards (one per line).', rows: 3 },
  { key: 'languages', label: 'Languages',
    helper: 'Spoken languages and fluency levels (one per line).', rows: 2 },
  { key: 'raw_notes', label: 'Other / Raw Notes',
    helper: 'Anything else you want our matcher to know. Stays empty unless you add something.',
    rows: 4 },
];

const CONTACT_FIELDS = [
  { key: 'name', label: 'Full Name', placeholder: 'Jane Doe' },
  { key: 'email', label: 'Email', placeholder: 'jane@example.com' },
  { key: 'phone', label: 'Phone', placeholder: '+49 …' },
  { key: 'location', label: 'Location', placeholder: 'Berlin, Germany' },
  { key: 'linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/in/…' },
];

// CV sections rendered as line-based editable fields.
const ARRAY_SECTION_KEYS = new Set([
  'skills', 'projects', 'certifications', 'languages',
]);

const safeCvReviewText = (value) => toSafeDisplayString(value, { context: 'find-jobs-submit' });
const BULLET_PREFIX_RE = /^\s*(?:[-*•‣◦▪]|\d+[.)])\s+/;
const AUTOSAVE_DELAY_MS = 1000;
const AUTOSAVE_MAX_RETRIES = 3;
const AUTOSAVE_RETRY_BASE_MS = 2000;
const AUTOSAVE_RETRY_MAX_MS = 12000;

// -- Structured Experience / Education schemas -------------------------
//
// Experience and Education use repeatable cards with separate fields.
const EXPERIENCE_FIELDS = [
  { key: 'company',  label: 'Company',       placeholder: 'Acme GmbH' },
  { key: 'position', label: 'Position',      placeholder: 'Senior Software Engineer' },
  { key: 'location', label: 'Location',      placeholder: 'Berlin, Germany' },
  { key: 'dates',    label: 'Dates',         placeholder: 'Jan 2022 - Present' },
];
const EXPERIENCE_REQUIRED_KEYS = ['position', 'company', 'dates'];
const EXPERIENCE_REQUIRED_LABELS = { position: 'Position', company: 'Company', dates: 'Dates' };
const WARM_INPUT_CLASS = 'mt-1 rounded-xl border-[#d8c8ab] bg-white/70 focus:border-[#173d35] focus:ring-[#173d35]/20';
const EDUCATION_FIELDS = [
  { key: 'institution', label: 'Institution', placeholder: 'TU Berlin' },
  { key: 'degree',      label: 'Degree',      placeholder: 'MSc' },
  { key: 'field',       label: 'Field of Study', placeholder: 'Computer Science' },
  { key: 'location',    label: 'Location',    placeholder: 'Berlin, Germany' },
  { key: 'dates',       label: 'Dates',       placeholder: '2016 - 2018' },
];

function blankExperienceEntry() {
  return { company: '', position: '', location: '', dates: '', details: '', bullets: '' };
}
function experienceEntryHasContent(entry) {
  return Boolean(entry && Object.values(entry).some((value) => safeFormText(value)));
}

function missingExperienceFields(entry) {
  if (!experienceEntryHasContent(entry)) return [];
  return EXPERIENCE_REQUIRED_KEYS.filter((key) => !safeFormText(entry[key]));
}

function isExperienceEntryComplete(entry) {
  return experienceEntryHasContent(entry) && missingExperienceFields(entry).length === 0;
}

function experienceSectionStatus(entries) {
  const visible = (Array.isArray(entries) ? entries : []).filter(experienceEntryHasContent);
  if (!visible.length) return 'Needs review';
  return visible.every(isExperienceEntryComplete) ? 'Complete' : 'Needs review';
}

function blankEducationEntry() {
  return { institution: '', degree: '', field: '', location: '', dates: '', details: '' };
}

function blankProjectEntry() {
  return { title: '', role: '', tools: '', dates: '', description: '', bullets: '', links: '' };
}

function safeFormText(value) {
  if (Array.isArray(value)) return value.map((item) => safeFormText(item)).filter(Boolean).join('\n');
  if (value && typeof value === 'object') return toSafeDisplayString(value, { context: 'cv-preview' });
  return String(value || '').trim();
}

function cleanPhoneText(value) {
  return safeFormText(value)
    .replace(/[()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripBulletPrefix(value) {
  return safeFormText(value).replace(BULLET_PREFIX_RE, '').trim();
}

function splitDetailsAndBullets(value) {
  const details = [];
  const bullets = [];
  safeFormText(value).split(/\r?\n/).forEach((line) => {
    const text = line.trim();
    if (!text) return;
    if (BULLET_PREFIX_RE.test(text)) bullets.push(stripBulletPrefix(text));
    else details.push(text);
  });
  return { details: details.join('\n'), bullets: bullets.filter(Boolean) };
}

function normalizeBulletTextarea(value) {
  if (Array.isArray(value)) return value.map(stripBulletPrefix).filter(Boolean).join('\n');
  return safeFormText(value).split(/\r?\n/).map(stripBulletPrefix).filter(Boolean).join('\n');
}

function projectToText(project) {
  if (!project || typeof project !== 'object' || Array.isArray(project)) return safeFormText(project);
  const title = safeFormText(project.title || project.name);
  const role = safeFormText(project.role);
  const tools = Array.isArray(project.tools) ? project.tools.map(safeFormText).filter(Boolean).join(', ') : safeFormText(project.tools || project.tech_stack || project.stack);
  const dates = safeFormText(project.dates);
  const description = safeFormText(project.description || project.summary || project.details);
  const bullets = Array.isArray(project.bullets || project.highlights)
    ? (project.bullets || project.highlights).map((b) => `- ${stripBulletPrefix(b)}`).filter(Boolean)
    : normalizeBulletTextarea(project.bullets || project.highlights).split(/\r?\n/).filter(Boolean).map((b) => `- ${b}`);
  const links = Array.isArray(project.links || project.urls) ? (project.links || project.urls).map(safeFormText).filter(Boolean) : [];
  return [title, role && `Role: ${role}`, tools && `Tools: ${tools}`, dates && `Dates: ${dates}`, description, ...bullets, ...links].filter(Boolean).join('\n');
}

function parseProjectBlock(block) {
  const lines = safeFormText(block).split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return null;
  const project = { title: '', description: '', bullets: [], links: [] };
  const description = [];
  lines.forEach((line) => {
    if (BULLET_PREFIX_RE.test(line)) {
      project.bullets.push(stripBulletPrefix(line));
      return;
    }
    const match = line.match(/^\s*(role|tools?|tech(?: stack)?|stack|dates?)\s*:\s*(.+)$/i);
    if (match) {
      const key = match[1].toLowerCase();
      const value = safeFormText(match[2]);
      if (key.startsWith('role')) project.role = value;
      else if (key.startsWith('date')) project.dates = value;
      else project.tools = value;
      return;
    }
    if (/^https?:\/\//i.test(line)) {
      project.links.push(line);
      return;
    }
    if (!project.title && line.length <= 160) project.title = line;
    else description.push(line);
  });
  project.description = description.join('\n');
  return Object.values(project).some((value) => Array.isArray(value) ? value.length : safeFormText(value)) ? project : null;
}

function loadProjectEntries(value) {
  if (!Array.isArray(value) || value.length === 0) return [blankProjectEntry()];
  return value.map((item) => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      return {
        title: safeFormText(item.title || item.name),
        role: safeFormText(item.role),
        tools: Array.isArray(item.tools) ? item.tools.map(safeFormText).filter(Boolean).join(', ') : safeFormText(item.tools || item.tech_stack || item.stack),
        dates: safeFormText(item.dates || item.date_range_display),
        description: safeFormText(item.description || item.summary || item.details),
        bullets: normalizeBulletTextarea(item.bullets || item.highlights),
        links: Array.isArray(item.links || item.urls) ? (item.links || item.urls).map(safeFormText).filter(Boolean).join('\n') : safeFormText(item.links || item.urls),
      };
    }
    const parsed = parseProjectBlock(item) || {};
    return {
      ...blankProjectEntry(),
      title: safeFormText(parsed.title),
      role: safeFormText(parsed.role),
      tools: safeFormText(parsed.tools),
      dates: safeFormText(parsed.dates),
      description: safeFormText(parsed.description || (!parsed.title ? item : '')),
      bullets: normalizeBulletTextarea(parsed.bullets),
      links: Array.isArray(parsed.links) ? parsed.links.join('\n') : safeFormText(parsed.links),
    };
  }).filter((entry) => Object.values(entry).some(Boolean)) || [blankProjectEntry()];
}

function projectEntryToWire(entry) {
  return {
    title: safeFormText(entry.title),
    role: safeFormText(entry.role),
    tools: safeFormText(entry.tools),
    dates: safeFormText(entry.dates),
    description: safeFormText(entry.description),
    bullets: normalizeBulletTextarea(entry.bullets).split(/\r?\n/).filter(Boolean),
    links: safeFormText(entry.links).split(/\r?\n/).map(safeFormText).filter(Boolean),
  };
}

function draftUpdatedAt(value) {
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
}

function browserDraftKey(userId, submissionId) {
  if (!userId || !submissionId) return '';
  return `jobmatchpro:master-cv-draft:${userId}:${submissionId}`;
}

function readBrowserDraft(userId, submissionId) {
  if (typeof window === 'undefined' || !userId || !submissionId) return null;
  try {
    const raw = window.localStorage.getItem(browserDraftKey(userId, submissionId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || parsed.user_id !== userId || parsed.submission_id !== submissionId || !parsed.canonical_cv_json) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeBrowserDraft(userId, submissionId, canonical_cv_json, serverUpdatedAt = null, preferences = null, candidateSnapshotState = null) {
  if (typeof window === 'undefined' || !userId || !submissionId) return;
  try {
    window.localStorage.setItem(browserDraftKey(userId, submissionId), JSON.stringify({
      schema_version: 'master-cv-browser-draft-v1',
      user_id: userId,
      submission_id: submissionId,
      saved_at: new Date().toISOString(),
      server_updated_at: serverUpdatedAt || null,
      canonical_cv_json,
      preferences: preferences && typeof preferences === 'object' ? preferences : undefined,
      candidate_snapshot_state: candidateSnapshotState && typeof candidateSnapshotState === 'object' ? candidateSnapshotState : undefined,
    }));
  } catch (_) {
    return;
  }
}

function clearBrowserDraft(userId, submissionId) {
  if (typeof window === 'undefined' || !userId || !submissionId) return;
  try {
    window.localStorage.removeItem(browserDraftKey(userId, submissionId));
  } catch (_) {
    return;
  }
}

function extractorItems(review) {
  if (!review || typeof review !== 'object') return [];
  const output = [];
  const pushOne = (section, item) => {
    if (!item || typeof item !== 'object') return;
    const value = safeFormText(item.value);
    if (!value) return;
    output.push({ section, value, evidence: safeFormText(item.evidence_text), confidence: safeFormText(item.confidence || 'Needs review'), source: safeFormText(item.source_section) });
  };
  pushOne('Current role', review.current_role);
  pushOne('Experience summary', review.experience_summary);
  ['education', 'skills', 'tools', 'languages', 'projects', 'certifications'].forEach((section) => {
    const entries = Array.isArray(review[section]) ? review[section] : [];
    entries.slice(0, 8).forEach((item) => pushOne(section, item));
  });
  return output.slice(0, 30);
}

function extractorKey(item) {
  return `${item.section}:${item.value}`;
}

function preferenceSummaryRows(preferences) {
  return [
    ['Target roles', preferences.target_roles],
    ['Target locations', preferences.target_locations],
    ['Seniority', preferences.target_seniority],
    ['Current job title', preferences.job_title],
    ['Current location', preferences.location],
    ['Years of experience', preferences.total_experience || preferences.years_experience_band],
    ['Salary expectation', preferences.salary_expectation],
    ['Visa status', preferences.visa_status],
    ['Skills / core skills', preferences.core_skills],
    ['German level', preferences.german_level],
    ['Other languages', preferences.languages],
    ['Education', preferences.education],
  ].map(([label, value]) => [label, safeFormText(value)]).filter(([, value]) => value);
}

function sectionStatusFromContent(value) {
  if (Array.isArray(value)) return value.length ? 'Complete' : 'Empty';
  return safeFormText(value) ? 'Complete' : 'Empty';
}

function candidateEvidenceStatus(confidence, decision) {
  if (decision === 'ignore') return 'Ignored';
  if (decision === 'remove') return 'Removed';
  if (decision === 'confirm') return 'Found';
  const clean = safeFormText(confidence).toLowerCase();
  if (!clean || clean.includes('need') || clean.includes('low') || clean.includes('fallback')) return 'Needs review';
  return 'Found';
}

function candidateAiStatus(value) {
  const clean = safeFormText(value).toLowerCase();
  if (!clean || clean.includes('unavailable') || clean.includes('fallback') || clean.includes('error')) return 'CV review is limited right now';
  return 'Evidence review ready';
}

function statusPillClass(status) {
  if (status === 'Complete' || status === 'Found' || status === 'Ready to confirm') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'Updated') return 'border-[#d8c8ab] bg-[#fff4dc] text-[#6f5b2f]';
  if (status === 'Ignored' || status === 'Removed') return 'border-slate-200 bg-slate-50 text-slate-600';
  return 'border-amber-200 bg-amber-50 text-amber-800';
}

function SectionStatus({ status, testid }) {
  return <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${statusPillClass(status)}`} data-testid={testid}>{status}</span>;
}

function SummaryChips({ label, values, emptyText, testid }) {
  const items = splitList(values);
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.14em] text-[#7b694d]">{label}</p>
      <div className="flex flex-wrap gap-2">
        {items.length ? items.map((item) => (
          <span key={`${label}-${item}`} className="rounded-full border border-[#d7c5a7] bg-white/70 px-3 py-1.5 text-sm text-[#30473e]">
            {safeCvReviewText(item)}
          </span>
        )) : (
          <span className="rounded-full border border-[#d7c5a7] bg-[#fff4dc] px-3 py-1.5 text-sm text-[#6f5b2f]">{emptyText}</span>
        )}
      </div>
    </div>
  );
}

function PreferenceSummary({ preferences }) {
  const secondary = preferenceSummaryRows(preferences).filter(([label]) => !['Target roles', 'Target locations', 'Seniority', 'Skills / core skills'].includes(label));
  return (
    <div className="space-y-5" data-testid="draft-preferences-summary">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SummaryChips label="Roles" values={preferences.target_roles} emptyText="Add roles" testid="master-cv-search-roles" />
        <SummaryChips label="Markets" values={preferences.target_locations} emptyText="Add markets" testid="master-cv-search-markets" />
        <SummaryChips label="Level" values={preferences.target_seniority} emptyText="Optional" testid="master-cv-search-level" />
        <SummaryChips label="Skills" values={preferences.core_skills} emptyText="Optional" testid="master-cv-search-skills" />
      </div>
      {secondary.length ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {secondary.map(([label, value]) => (
            <div key={label} className="rounded-xl border border-[#d8c8ab] bg-white/45 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.14em] text-[#7b694d]">{label}</p>
              <p className="mt-1 text-sm text-[#30473e]">{safeCvReviewText(value)}</p>
            </div>
          ))}
        </div>
      ) : (
        null
      )}
    </div>
  );
}

function confirmedExtractorItems(review, decisions) {
  return extractorItems(review).filter((item) => (decisions[extractorKey(item)] || 'pending') === 'confirm');
}

function candidateSnapshotSignature(candidateIntakeDraft, preferences, selectedEvidenceKeys) {
  const insights = candidateIntakeDraft?.candidateInsights || {};
  const agent = candidateIntakeDraft?.careerAgent || {};
  return JSON.stringify({
    roles: splitList(preferences?.target_roles),
    markets: splitList(preferences?.target_locations),
    workTypes: uniqueArray(agent.workTypes || []),
    motivations: uniqueArray(agent.motivations || []),
    waysOfWorking: uniqueArray(insights.waysOfWorking || []),
    reasonForLooking: insights.reasonForLooking || '',
    availability: insights.availability || '',
    noticePeriod: insights.noticePeriod || '',
    selectedEvidenceKeys: uniqueArray(selectedEvidenceKeys || []),
  });
}

function buildCandidateSnapshotSections(candidateIntakeDraft, preferences, selectedEvidence) {
  const insights = candidateIntakeDraft?.candidateInsights || {};
  const agent = candidateIntakeDraft?.careerAgent || {};
  const directionLines = [
    splitList(preferences?.target_roles).length ? `Target roles: ${splitList(preferences.target_roles).join(', ')}` : '',
    splitList(preferences?.target_locations).length ? `Target markets: ${splitList(preferences.target_locations).join(', ')}` : '',
  ].filter(Boolean);
  const workPreferenceLines = [
    uniqueArray(agent.workTypes || []).length ? `Type of work: ${uniqueArray(agent.workTypes).join(', ')}` : '',
    uniqueArray(agent.motivations || []).length ? `Motivations: ${uniqueArray(agent.motivations).join(', ')}` : '',
    uniqueArray(insights.waysOfWorking || []).length ? `Ways of working: ${uniqueArray(insights.waysOfWorking).join(', ')}` : '',
  ].filter(Boolean);
  const reasonLines = [insights.reasonForLooking ? `Reason for looking: ${insights.reasonForLooking}` : ''].filter(Boolean);
  const availabilityLines = [
    insights.availability ? `Availability: ${insights.availability}` : '',
    insights.noticePeriod ? `Notice period: ${insights.noticePeriod}` : '',
  ].filter(Boolean);
  const evidenceLines = selectedEvidence.map((item) => `${item.label}: ${safeFormText(item.value)}`).filter(Boolean);
  return [
    directionLines.length ? { key: 'direction', title: 'Search direction', lines: directionLines } : null,
    workPreferenceLines.length ? { key: 'work_preferences', title: 'Work preferences', lines: workPreferenceLines } : null,
    reasonLines.length ? { key: 'reason_for_looking', title: 'Reason for looking', lines: reasonLines } : null,
    availabilityLines.length ? { key: 'availability', title: 'Availability', lines: availabilityLines } : null,
    evidenceLines.length ? { key: 'confirmed_evidence', title: 'Confirmed CV evidence', lines: evidenceLines } : null,
  ].filter(Boolean);
}

function CareerAgentReadyCard({ preferences, canonicalCvJson, extractorItemsCount }) {
  const roleCount = splitList(preferences.target_roles).length;
  const markets = splitList(preferences.target_locations);
  const skillCount = uniqueArray([
    ...splitList(preferences.core_skills),
    ...(Array.isArray(canonicalCvJson?.skills) ? canonicalCvJson.skills : []),
  ]).length;
  const needsReview = [
    !safeFormText(canonicalCvJson?.professional_summary),
    !Array.isArray(canonicalCvJson?.experience) || !canonicalCvJson.experience.length,
    !Array.isArray(canonicalCvJson?.education) || !canonicalCvJson.education.length,
    !Array.isArray(canonicalCvJson?.projects) || !canonicalCvJson.projects.length,
    !Array.isArray(canonicalCvJson?.skills) || !canonicalCvJson.skills.length,
    !Array.isArray(canonicalCvJson?.certifications) || !canonicalCvJson.certifications.length,
    !Array.isArray(canonicalCvJson?.languages) || !canonicalCvJson.languages.length,
  ].filter(Boolean).length + extractorItemsCount;
  return (
    <section className="mb-6 rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-5" data-testid="career-agent-ready-review-card">
      <p className="text-xs font-medium uppercase tracking-[0.16em] text-[#7b694d]">Career Agent status</p>
      <h2 className="mt-1 font-serif-display text-2xl font-medium text-[#172820]">Your Career Agent is ready for review</h2>
      <p className="mt-2 text-sm leading-relaxed text-[#6f6657]">Review the evidence and fix anything missing before search starts.</p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryChips label="Target roles" values={roleCount ? [`${roleCount} roles`] : []} emptyText="Add roles" testid="ready-card-roles" />
        <SummaryChips label="Markets" values={markets} emptyText="Add markets" testid="ready-card-markets" />
        <SummaryChips label="CV" values={['CV added']} emptyText="CV not added" testid="ready-card-cv" />
        <SummaryChips label="Skills" values={skillCount ? [`${skillCount} skills`] : []} emptyText="Needs review" testid="ready-card-skills" />
        <SummaryChips label="Review" values={[`${needsReview} sections need review`]} emptyText="Review ready" testid="ready-card-review" />
      </div>
    </section>
  );
}

// Convert imported CV entries into editable cards without dropping irregular evidence.
function loadStructuredEntries(raw, blankFactory) {
  if (!Array.isArray(raw) || raw.length === 0) return [blankFactory()];

  const entries = raw.map((item) => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      const normalized = { ...blankFactory() };
      Object.keys(normalized).forEach((key) => {
        normalized[key] = key === 'bullets' ? normalizeBulletTextarea(item[key]) : safeFormText(item[key]);
      });
      if ('bullets' in normalized && !normalized.bullets) {
        const split = splitDetailsAndBullets(normalized.details);
        normalized.details = split.details;
        normalized.bullets = split.bullets.join('\n');
      }
      if ('bullets' in normalized && normalized.bullets) {
        normalized.details = [normalized.details, normalizeBulletTextarea(normalized.bullets).split(/\r?\n/).filter(Boolean).map((bullet) => `- ${bullet}`).join('\n')].filter(Boolean).join('\n');
        normalized.bullets = '';
      }
      const preservedEvidence = safeFormText(item.source_evidence);
      const hasStructuredValue = Object.values(normalized).some((value) => safeFormText(value));
      if (preservedEvidence || !hasStructuredValue) {
        const sourceEvidence = preservedEvidence || safeFormText(item);
        normalized.details = normalized.details || sourceEvidence;
        normalized.source_evidence = sourceEvidence;
        normalized.unresolved_evidence = true;
      }
      return normalized;
    }

    const sourceEvidence = safeFormText(item);
    if (!sourceEvidence) return null;
    const split = splitDetailsAndBullets(sourceEvidence);
    return {
      ...blankFactory(),
      details: [split.details, split.bullets.map((bullet) => `- ${bullet}`).join('\n')].filter(Boolean).join('\n'),
      source_evidence: sourceEvidence,
      unresolved_evidence: true,
    };
  }).filter(Boolean);

  return entries.length ? entries : [blankFactory()];
}

// Drop entries where every field is blank so we don't send empty objects on save.
function filterNonEmptyEntries(entries) {
  return entries.filter((e) => Object.values(e).some((v) => String(v || '').trim()));
}

// Format a single experience/education entry as a multi-line text block
// so buildCanonicalText's 500-char usability gate reflects structured content.
function formatExperienceEntry(e) {
  const header = [
    safeFormText(e.position),
    safeFormText(e.company) && `at ${safeFormText(e.company)}`,
    safeFormText(e.location) && `· ${safeFormText(e.location)}`,
    safeFormText(e.dates) && `(${safeFormText(e.dates)})`,
  ].filter(Boolean).join(' ');
  const details = safeFormText(e.details);
  const bullets = normalizeBulletTextarea(e.bullets).split(/\r?\n/).filter(Boolean).map((bullet) => `- ${bullet}`).join('\n');
  return [header, details, bullets].filter(Boolean).join('\n');
}
function formatEducationEntry(e) {
  const header = [
    safeFormText(e.degree),
    safeFormText(e.field) && `in ${safeFormText(e.field)}`,
    safeFormText(e.institution) && `- ${safeFormText(e.institution)}`,
    safeFormText(e.location) && `· ${safeFormText(e.location)}`,
    safeFormText(e.dates) && `(${safeFormText(e.dates)})`,
  ].filter(Boolean).join(' ');
  const details = safeFormText(e.details);
  return [header, details].filter(Boolean).join('\n');
}

// -- Helpers -----------------------------------------------------------

function arrayToTextarea(value) {
  if (Array.isArray(value) && value.some((item) => item && typeof item === 'object')) {
    return value.map(projectToText).filter(Boolean).join('\n\n');
  }
  if (Array.isArray(value)) return value.map((s) => safeFormText(s)).filter(Boolean).join('\n');
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return safeFormText(value);
  return '';
}

function textareaToArray(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function textareaToProjects(value) {
  const text = safeFormText(value);
  if (!text) return [];
  const blocks = text.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);
  if (blocks.length === 1) {
    const lines = blocks[0].split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const hasProjectShape = lines.some((line) => BULLET_PREFIX_RE.test(line) || /^(role|tools?|tech(?: stack)?|stack|dates?)\s*:/i.test(line));
    return hasProjectShape ? [parseProjectBlock(blocks[0])].filter(Boolean) : textareaToArray(text);
  }
  return blocks.map(parseProjectBlock).filter(Boolean);
}

// Build a plain-text canonical_cv_text from the editable form so the
// backend's is_usable_cv_text gate is satisfied. Mirrors backend
// build_canonical_cv_text_from_json structurally.
function buildCanonicalText({ contact, sections, experienceEntries, educationEntries }) {
  const parts = [];
  const contactLines = CONTACT_FIELDS
    .map((f) => safeFormText(contact[f.key]) && `${f.label}: ${safeFormText(contact[f.key])}`)
    .filter(Boolean);
  if (contactLines.length) parts.push(['Contact', contactLines.join('\n')].join('\n'));
  // professional_summary first to match the original top-to-bottom order.
  const summary = safeFormText(sections.professional_summary);
  if (summary) parts.push(`Professional Summary\n${summary}`);
  // Structured Experience block
  const expLines = filterNonEmptyEntries(experienceEntries).map(formatExperienceEntry).filter(Boolean);
  if (expLines.length) parts.push(['Work Experience', expLines.join('\n\n')].join('\n'));
  // Structured Education block
  const eduLines = filterNonEmptyEntries(educationEntries).map(formatEducationEntry).filter(Boolean);
  if (eduLines.length) parts.push(['Education', eduLines.join('\n\n')].join('\n'));
  // Remaining SECTIONS (skills, projects, certifications, languages, raw_notes)
  SECTIONS.forEach(({ key, label }) => {
    if (key === 'professional_summary') return;
    const v = safeFormText(sections[key]);
    if (v) parts.push(`${label}\n${v}`);
  });
  return parts.join('\n\n');
}

function initialContactFromJson(json) {
  const c = (json && typeof json.contact === 'object' && json.contact) || {};
  const out = {};
  CONTACT_FIELDS.forEach(({ key }) => { out[key] = key === 'phone' ? cleanPhoneText(c[key]) : safeFormText(c[key]); });
  return out;
}

function initialSectionsFromJson(json) {
  const out = {};
  SECTIONS.forEach(({ key }) => {
    const raw = json ? json[key] : '';
    out[key] = ARRAY_SECTION_KEYS.has(key) ? arrayToTextarea(raw) : safeFormText(raw);
  });
  return out;
}

// Convert the editable form back into the current saved shape.
function sectionsToWireShape(sections) {
  const out = {};
  SECTIONS.forEach(({ key }) => {
    if (ARRAY_SECTION_KEYS.has(key)) {
      out[key] = key === 'projects' ? textareaToProjects(sections[key]) : textareaToArray(sections[key]);
    } else {
      out[key] = (sections[key] || '').trim();
    }
  });
  return out;
}

// -- Job preferences (Review job preferences card) --------------------
const PREFERENCE_FIELDS = [
  { key: 'job_title', label: 'Current job title', placeholder: 'Business Analyst' },
  { key: 'location', label: 'Current location', placeholder: 'Berlin, Germany' },
  { key: 'target_roles', label: 'Target roles',
    placeholder: 'Business Analyst Intern, Junior Product Manager, Data Analyst',
    helper: 'Be specific. Example: Business Analyst Intern, Junior Product Manager, Data Analyst.',
    testid: 'draft-preferences-target-roles' },
  { key: 'target_locations', label: 'Target locations',
    placeholder: 'Berlin, Munich, Remote (DE)',
    testid: 'draft-preferences-target-locations' },
  { key: 'target_seniority', label: 'Seniority',
    placeholder: 'Intern, Junior, Mid-level' },
  { key: 'total_experience', label: 'Total experience', placeholder: '5 years' },
  { key: 'years_experience_band', label: 'Experience band', placeholder: '3-5' },
  { key: 'german_level', label: 'German level',
    placeholder: 'A2 / B1 / B2 / C1' },
  { key: 'languages', label: 'Other languages', placeholder: 'English C1, French A2' },
  { key: 'visa_status', label: 'Visa status',
    placeholder: 'Student visa, EU citizen, Job seeker visa',
    helper: 'This helps your agent avoid roles that clearly cannot support your work situation. You can change it anytime.' },
  { key: 'salary_expectation', label: 'Salary expectation (€/yr)',
    placeholder: '50000', type: 'number', helper: 'Optional. Used only to avoid obviously bad-fit roles.' },
  { key: 'core_skills', label: 'Skills / core skills',
    placeholder: 'Python, SQL, Excel, communication' },
  { key: 'education', label: 'Education', placeholder: 'MSc Data Science' },
];

function initialPreferenceState(initial) {
  const out = {};
  PREFERENCE_FIELDS.forEach(({ key }) => {
    const v = initial && initial[key];
    out[key] = v == null ? '' : String(v);
  });
  return out;
}

// Build the preference update payload using the existing saved field names.
function preferencesPayload(values) {
  const out = {};
  PREFERENCE_FIELDS.forEach(({ key, type }) => {
    const v = values[key];
    if (key === 'salary_expectation') {
      const n = parseInt(v, 10);
      out[key] = Number.isFinite(n) && n > 0 ? n : null;
    } else if (type === 'number') {
      const n = Number(v);
      out[key] = Number.isFinite(n) ? n : null;
    } else {
      out[key] = (v || '').trim();
    }
  });
  return out;
}

function preferenceStateFromAny(value) {
  const source = value && typeof value === 'object' ? value : {};
  const out = initialPreferenceState(source);
  if (Array.isArray(source.target_seniority_levels) && source.target_seniority_levels.length) {
    out.target_seniority = source.target_seniority_levels.join(', ');
  } else if (Array.isArray(source.target_seniority)) {
    out.target_seniority = source.target_seniority.join(', ');
  }
  return out;
}

function isMissingDraftError(err) {
  const status = Number(err?.status);
  if (status === 404) return true;
  if (status === 401 || status === 403 || status >= 500) return false;
  const rawDetail = [
    err?.message,
    err?.payload?.detail,
    err?.payload?.message,
    err?.payload?.error,
  ].filter((value) => typeof value === 'string').join(' ').toLowerCase();
  return rawDetail.includes('submission not found')
    || rawDetail.includes('draft not found')
    || rawDetail.includes('cv draft not found')
    || rawDetail.includes('record not found')
    || rawDetail.includes('missing record');
}

// -- Component ---------------------------------------------------------

export default function MasterCvReview({ submissionId, userId, initialPreferences, candidateIntakeDraft, onConfirmed, onBackToForm, onMissingDraft, onStartExactJob, onConfirmMasterProfile }) {
  const isProfileOnly = typeof onConfirmMasterProfile === 'function';
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [rawCvText, setRawCvText] = useState('');
  const [contact, setContact] = useState(() => initialContactFromJson({}));
  const [sections, setSections] = useState(() => initialSectionsFromJson({}));
  const [experienceEntries, setExperienceEntries] = useState(() => [blankExperienceEntry()]);
  const [educationEntries, setEducationEntries] = useState(() => [blankEducationEntry()]);
  const [projectEntries, setProjectEntries] = useState(() => [blankProjectEntry()]);
  const [parserWarnings, setParserWarnings] = useState([]);
  const [extractorReview, setExtractorReview] = useState(null);
  const [extractorDecisions, setExtractorDecisions] = useState({});
  const [strongestEvidenceKeys, setStrongestEvidenceKeys] = useState([]);
  const [snapshotApprovals, setSnapshotApprovals] = useState({});
  const [dismissedSearchSkills, setDismissedSearchSkills] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [submitState, setSubmitState] = useState('idle'); // 'idle'|'saving'|'starting'|'error'
  const [submitError, setSubmitError] = useState('');
  // Draft job-search preferences (Review job preferences card)
  const [preferences, setPreferences] = useState(() => preferenceStateFromAny(initialPreferences || {}));
  const [savedPreferences, setSavedPreferences] = useState(() => preferenceStateFromAny(initialPreferences || {}));
  const [isPreferenceEditing, setIsPreferenceEditing] = useState(false);
  const [prefSaveState, setPrefSaveState] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const [prefError, setPrefError] = useState('');
  const [prefSearchAlreadyStarted, setPrefSearchAlreadyStarted] = useState(false);
  const [autosaveState, setAutosaveState] = useState('clean'); // clean|dirty|saving|saved|retrying|locally_saved|conflict|error
  const [resumeNotice, setResumeNotice] = useState('');
  const [conflictMessage, setConflictMessage] = useState('');
  const autosaveTimerRef = useRef(null);
  const autosaveAbortRef = useRef(null);
  const retryCountRef = useRef(0);
  const serverUpdatedAtRef = useRef(null);
  const channelRef = useRef(null);
  const stoppedRef = useRef(false);
  const initializedRef = useRef(false);
  const latestSaveRevisionRef = useRef(0);
  const latestSnapshotRef = useRef('');
  const preferenceAutosaveTimerRef = useRef(null);
  const latestFormRef = useRef({ canonicalCvJson: null, canonicalCvText: '', canonicalSnapshot: '' });
  const latestCandidateSnapshotStateRef = useRef({ strongestEvidenceKeys: [], approvals: {}, sourceSignature: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const data = await fetchCanonicalCv(submissionId);
        if (cancelled) return;
        const json = data.canonical_cv_json && Object.keys(data.canonical_cv_json).length
          ? data.canonical_cv_json
          : null;
        const browserDraft = readBrowserDraft(userId, submissionId);
        const shouldResumeBrowserDraft = browserDraft
          && draftUpdatedAt(browserDraft.saved_at) > draftUpdatedAt(data.updated_at);
        const activeJson = shouldResumeBrowserDraft ? browserDraft.canonical_cv_json : json;
        if (shouldResumeBrowserDraft) {
          setResumeNotice('Unsaved Master CV changes from this browser were restored.');
          if (browserDraft.preferences && typeof browserDraft.preferences === 'object') {
            const restoredPrefs = preferenceStateFromAny(browserDraft.preferences);
            setPreferences(restoredPrefs);
            setSavedPreferences(restoredPrefs);
          }
          if (browserDraft.candidate_snapshot_state && typeof browserDraft.candidate_snapshot_state === 'object') {
            setStrongestEvidenceKeys(uniqueArray(browserDraft.candidate_snapshot_state.strongestEvidenceKeys || []));
            setSnapshotApprovals(
              browserDraft.candidate_snapshot_state.approvals && typeof browserDraft.candidate_snapshot_state.approvals === 'object'
                ? browserDraft.candidate_snapshot_state.approvals
                : {},
            );
          }
        } else {
          setResumeNotice('');
        }
        serverUpdatedAtRef.current = data.updated_at || null;
        setRawCvText(safeFormText(data.raw_cv_text));
        setContact(initialContactFromJson(activeJson || {}));
        // Backend pre-fills canonical_cv_json from the extracted CV + intake fields,
        // so the user lands on a populated form. If json is genuinely empty (e.g.
        // CV extraction failed), each section stays blank - the user starts from
        // the raw extract reference shown above.
        setSections(initialSectionsFromJson(activeJson || {}));
        setExperienceEntries(loadStructuredEntries(activeJson?.experience, blankExperienceEntry));
        setEducationEntries(loadStructuredEntries(activeJson?.education, blankEducationEntry));
        setProjectEntries(loadProjectEntries(activeJson?.projects));
        setParserWarnings(Array.isArray(activeJson?.parser_warnings) ? activeJson.parser_warnings.map(safeFormText).filter(Boolean) : []);
        setExtractorReview(activeJson?.phase2_cv_extractor_review && typeof activeJson.phase2_cv_extractor_review === 'object' ? activeJson.phase2_cv_extractor_review : null);
        latestSnapshotRef.current = '';
        initializedRef.current = true;
        setAutosaveState(shouldResumeBrowserDraft ? 'locally_saved' : 'clean');
      } catch (err) {
        if (!cancelled && isMissingDraftError(err) && onMissingDraft) {
          onMissingDraft();
          return;
        }
        if (!cancelled) setLoadError(getSafeErrorMessage(err, { context: 'cv-preview' }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId, userId, onMissingDraft]);

  // Wire shape: arrays of OBJECTS for experience/education (new structured shape);
  // arrays of STRINGS for skills/projects/certifications/languages;
  // strings for professional_summary / raw_notes. Backend's canonical_cv_json
  // JSONB column accepts both - the shape change is opaque to it.
  const canonicalCvJson = useMemo(
    () => {
      const structuredExperience = filterNonEmptyEntries(experienceEntries).map((entry) => {
        const explicitBullets = normalizeBulletTextarea(entry.bullets).split(/\r?\n/).filter(Boolean);
        const { details, bullets } = splitDetailsAndBullets(entry.details);
        return {
          ...entry,
          details,
          bullets: explicitBullets.length ? explicitBullets : bullets,
        };
      });
      const output = {
      schema_version: 'canonical-cv-v1',
      contact,
      ...sectionsToWireShape(sections),
      experience: structuredExperience,
      education: filterNonEmptyEntries(educationEntries),
      projects: projectEntries.map(projectEntryToWire).filter((entry) => Object.values(entry).some((value) => Array.isArray(value) ? value.length : value)),
      };
      if (extractorReview) output.phase2_cv_extractor_review = extractorReview;
      return output;
    },
    [contact, sections, experienceEntries, educationEntries, projectEntries, extractorReview],
  );
  const canonicalCvText = useMemo(
    () => buildCanonicalText({ contact, sections: { ...sections, projects: projectEntries.map(projectToText).filter(Boolean).join('\n\n') }, experienceEntries, educationEntries }),
    [contact, sections, experienceEntries, educationEntries, projectEntries],
  );
  const canonicalSnapshot = useMemo(() => JSON.stringify(canonicalCvJson), [canonicalCvJson]);

  const runAutosave = useCallback(async (options = {}) => {
    if (!submissionId || stoppedRef.current) return false;
    const { force = false } = options;
    const saveRevision = latestSaveRevisionRef.current + 1;
    latestSaveRevisionRef.current = saveRevision;
    if (autosaveAbortRef.current) autosaveAbortRef.current.abort();
    const controller = new AbortController();
    autosaveAbortRef.current = controller;
    const snapshot = latestFormRef.current;
    setAutosaveState(force ? 'saving' : (retryCountRef.current > 0 ? 'retrying' : 'saving'));
    try {
      const result = await saveCanonicalCv(submissionId, {
        canonical_cv_json: snapshot.canonicalCvJson,
        canonical_cv_text: snapshot.canonicalCvText,
        cv_review_status: 'needs_review',
        save_mode: 'autosave',
        expected_updated_at: serverUpdatedAtRef.current,
        force_overwrite: force,
        signal: controller.signal,
      });
      if (saveRevision !== latestSaveRevisionRef.current) return true;
      serverUpdatedAtRef.current = result.updated_at || serverUpdatedAtRef.current;
      retryCountRef.current = 0;
      setAutosaveState('saved');
      setSavedAt(new Date());
      setConflictMessage('');
      writeBrowserDraft(userId, submissionId, snapshot.canonicalCvJson, serverUpdatedAtRef.current, preferences, latestCandidateSnapshotStateRef.current);
      if (channelRef.current) {
        channelRef.current.postMessage({ type: 'master-cv-saved', submissionId, updatedAt: serverUpdatedAtRef.current });
      }
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return false;
      writeBrowserDraft(userId, submissionId, snapshot.canonicalCvJson, serverUpdatedAtRef.current, preferences, latestCandidateSnapshotStateRef.current);
      if (err?.status === 409) {
        setAutosaveState('conflict');
        setConflictMessage('This Master CV was updated in another tab or device. Review before overwriting.');
        return false;
      }
      const nextRetry = retryCountRef.current + 1;
      retryCountRef.current = nextRetry;
      if (nextRetry <= AUTOSAVE_MAX_RETRIES) {
        setAutosaveState('retrying');
        const delay = Math.min(AUTOSAVE_RETRY_MAX_MS, AUTOSAVE_RETRY_BASE_MS * nextRetry);
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = window.setTimeout(() => { void runAutosave(); }, delay);
      } else {
        setAutosaveState('locally_saved');
      }
      return false;
    }
  }, [submissionId, userId, preferences]);

  useEffect(() => {
    latestFormRef.current = { canonicalCvJson, canonicalCvText, canonicalSnapshot };
    if (!initializedRef.current || loading || stoppedRef.current || !submissionId) return;
    if (canonicalSnapshot === latestSnapshotRef.current) return;
    latestSnapshotRef.current = canonicalSnapshot;
    setAutosaveState('dirty');
    setConflictMessage('');
    writeBrowserDraft(userId, submissionId, canonicalCvJson, serverUpdatedAtRef.current, preferences, latestCandidateSnapshotStateRef.current);

    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      void runAutosave();
    }, AUTOSAVE_DELAY_MS);
  }, [canonicalSnapshot, canonicalCvJson, canonicalCvText, loading, submissionId, userId, runAutosave, preferences]);

  useEffect(() => {
    if (!submissionId || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(`jobmatchpro-master-cv-${submissionId}`);
    channelRef.current = channel;
    channel.onmessage = (event) => {
      if (event?.data?.type !== 'master-cv-saved') return;
      if (event.data.updatedAt && event.data.updatedAt !== serverUpdatedAtRef.current) {
        serverUpdatedAtRef.current = event.data.updatedAt;
        if (autosaveState === 'dirty' || autosaveState === 'saving') {
          setAutosaveState('conflict');
          setConflictMessage('This Master CV was saved in another tab. Continue here only if you want to overwrite those changes.');
        }
      }
    };
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [submissionId, autosaveState]);

  useEffect(() => {
    stoppedRef.current = false;
    return () => {
      stoppedRef.current = true;
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
      if (preferenceAutosaveTimerRef.current) window.clearTimeout(preferenceAutosaveTimerRef.current);
      if (autosaveAbortRef.current) autosaveAbortRef.current.abort();
    };
  }, []);
  // Non-blocking warning banner: show when the backend signalled the parser
  // couldn't find clear sections, OR when the user has 3+ empty major sections
  // (heuristic for CVs that slipped past the parser without obvious headers).
  const showParserWarning = useMemo(() => {
    if (parserWarnings.includes('no_clear_cv_sections_detected')) return true;
    const emptySummary = !(sections.professional_summary || '').trim();
    const emptySkills = !(sections.skills || '').trim();
    const emptyExperience = filterNonEmptyEntries(experienceEntries).length === 0;
    const emptyEducation = filterNonEmptyEntries(educationEntries).length === 0;
    const emptyMajor = [emptySummary, emptySkills, emptyExperience, emptyEducation].filter(Boolean).length;
    return emptyMajor >= 3;
  }, [parserWarnings, sections, experienceEntries, educationEntries]);
  // Backend's is_usable_cv_text uses MIN_CV_TEXT_LENGTH=500. Mirror that here so the
  // user can't pass the FE gate and then be rejected by /confirm-cv-and-start-search.
  const MIN_CANONICAL_CHARS = 500;
  const trimmedLen = canonicalCvText.trim().length;
  const hasUsableText = trimmedLen >= MIN_CANONICAL_CHARS;
  const charsRemaining = Math.max(0, MIN_CANONICAL_CHARS - trimmedLen);

  const handleContactChange = (key, value) => setContact((p) => ({ ...p, [key]: value }));
  const handleSectionChange = (key, value) => setSections((p) => ({ ...p, [key]: value }));
  const searchPreferenceSkills = useMemo(() => uniqueArray([
    ...splitList(initialPreferences?.core_skills),
    ...(candidateIntakeDraft?.searchPreferences?.skills || []),
  ]), [initialPreferences?.core_skills, candidateIntakeDraft]);
  const confirmedCvSkills = useMemo(() => splitList(sections.skills), [sections.skills]);
  const suggestedSearchSkills = useMemo(() => searchPreferenceSkills.filter((skill) => !confirmedCvSkills.some((existing) => existing.toLowerCase() === skill.toLowerCase())), [searchPreferenceSkills, confirmedCvSkills]);
  const addSearchSkillsToMasterCv = () => {
    const nextSkills = uniqueArray([...confirmedCvSkills, ...suggestedSearchSkills]);
    setSections((prev) => ({ ...prev, skills: nextSkills.join('\n') }));
    setDismissedSearchSkills(true);
  };

  // Structured Experience handlers -----------------------------------
  const updateExperienceField = (index, fieldKey, value) =>
    setExperienceEntries((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [fieldKey]: value } : e)),
    );
  const addExperienceEntry = () =>
    setExperienceEntries((prev) => [...prev, blankExperienceEntry()]);
  const removeExperienceEntry = (index) =>
    setExperienceEntries((prev) =>
      prev.length === 1 ? [blankExperienceEntry()] : prev.filter((_, i) => i !== index),
    );

  // Structured Education handlers ------------------------------------
  const updateEducationField = (index, fieldKey, value) =>
    setEducationEntries((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [fieldKey]: value } : e)),
    );
  const addEducationEntry = () =>
    setEducationEntries((prev) => [...prev, blankEducationEntry()]);
  const removeEducationEntry = (index) =>
    setEducationEntries((prev) =>
      prev.length === 1 ? [blankEducationEntry()] : prev.filter((_, i) => i !== index),
    );

  // Structured Project handlers --------------------------------------
  const updateProjectField = (index, fieldKey, value) =>
    setProjectEntries((prev) => prev.map((p, i) => (i === index ? { ...p, [fieldKey]: value } : p)));
  const addProjectEntry = () => setProjectEntries((prev) => [...prev, blankProjectEntry()]);
  const removeProjectEntry = (index) =>
    setProjectEntries((prev) => (prev.length === 1 ? [blankProjectEntry()] : prev.filter((_, i) => i !== index)));

  // -- Preferences (Review job preferences card) ---------------------
  const preferencesDirty = useMemo(() => {
    return PREFERENCE_FIELDS.some(({ key }) =>
      String(preferences[key] || '').trim() !== String(savedPreferences[key] || '').trim()
    );
  }, [preferences, savedPreferences]);

  const handlePreferenceChange = (key, value) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
    if (prefSaveState === 'saved') setPrefSaveState('idle');
  };

  // Returns true on success (or no-op when not dirty), false on a 409
  // search-already-started block. Errors set prefError; the Master CV
  // editing experience continues regardless.
  const savePreferences = useCallback(async () => {
    if (!preferencesDirty) return true;
    setPrefSaveState('saving');
    setPrefError('');
    try {
      const payload = preferencesPayload(preferences);
      const result = await updateDraftPreferences(submissionId, payload);
      // Backend echoes the saved row; capture the canonical values so the
      // dirty check resets cleanly.
      const echoed = result && (result.preferences || result.submission || result);
      const next = preferenceStateFromAny(echoed && typeof echoed === 'object' ? echoed : payload);
      setSavedPreferences(next);
      setPreferences(next);
      setPrefSaveState('saved');
      return true;
    } catch (err) {
      setPrefSaveState('error');
      if (err.status === 409) {
        setPrefSearchAlreadyStarted(true);
        setPrefError('Search has already started, so preferences can no longer be changed for this run.');
      } else if (err.status === 401) {
        setPrefError('Your session has expired. Please sign in again.');
      } else if (err.status === 404) {
        setPrefError('Preferences could not be saved (endpoint not found). Please refresh and try again.');
      } else {
        setPrefError('Could not save preferences. Please try again.');
      }
      return false;
    }
  }, [preferencesDirty, preferences, submissionId]);

  useEffect(() => {
    if (!initializedRef.current || loading || stoppedRef.current || !submissionId || !preferencesDirty || prefSearchAlreadyStarted) return undefined;
    setPrefSaveState('saving');
    writeBrowserDraft(userId, submissionId, latestFormRef.current.canonicalCvJson, serverUpdatedAtRef.current, preferences, latestCandidateSnapshotStateRef.current);
    if (preferenceAutosaveTimerRef.current) window.clearTimeout(preferenceAutosaveTimerRef.current);
    preferenceAutosaveTimerRef.current = window.setTimeout(() => {
      void savePreferences();
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (preferenceAutosaveTimerRef.current) window.clearTimeout(preferenceAutosaveTimerRef.current);
    };
  }, [preferences, preferencesDirty, loading, submissionId, userId, prefSearchAlreadyStarted, savePreferences]);

  const handleSaveDraft = async () => {
    setSubmitState('saving');
    setSubmitError('');
    try {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
      if (preferenceAutosaveTimerRef.current) window.clearTimeout(preferenceAutosaveTimerRef.current);
      if (preferencesDirty) {
        const prefsOk = await savePreferences();
        if (!prefsOk) throw new Error('Could not save preferences. Please try again.');
      }
      const result = await saveCanonicalCv(submissionId, {
        canonical_cv_json: canonicalCvJson,
        canonical_cv_text: canonicalCvText,
        cv_review_status: 'reviewed',
        save_mode: 'explicit',
        expected_updated_at: serverUpdatedAtRef.current,
      });
      serverUpdatedAtRef.current = result.updated_at || serverUpdatedAtRef.current;
      retryCountRef.current = 0;
      setAutosaveState('saved');
      setConflictMessage('');
      setSavedAt(new Date());
      setSubmitState('idle');
    } catch (err) {
      setSubmitState('error');
      if (err.status === 409) {
        setAutosaveState('conflict');
        setConflictMessage('This Master CV was updated elsewhere. Use Force overwrite only if this version should replace it.');
      }
      setSubmitError(describeStartSearchError(err, { stage: 'canonical_cv' }));
    }
  };

  const handleForceOverwrite = async () => {
    setConflictMessage('');
    const ok = await runAutosave({ force: true });
    if (!ok) {
      setSubmitError('Could not overwrite the remote Master CV yet. Please try again.');
    }
  };

  const handleBackToForm = () => {
    if (window.confirm('Start over? This clears only this active draft from this browser.')) {
      clearBrowserDraft(userId, submissionId);
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
      if (preferenceAutosaveTimerRef.current) window.clearTimeout(preferenceAutosaveTimerRef.current);
      if (autosaveAbortRef.current) autosaveAbortRef.current.abort();
      onBackToForm?.();
    }
  };

// -- Friendly error mapping for the save and start chain -------------
function describeStartSearchError(err, { stage } = {}) {
  const status = err?.status;
  const code = err?.payload?.code;
  const backendDetail = getSafeErrorMessage(err?.payload || err, { context: 'find-jobs-submit' });

  if (code === 'search_limit_reached' || status === 429) {
    return getSafeErrorMessage(err?.payload || err, { context: 'find-jobs-submit' }) ||
      'You already have a recent search. Please wait before starting another one.';
  }
  if (status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  if (status === 409) {
    // Backend's friendly conflict detail is usually safe to show
    // (e.g. "Please review and save your Master CV before starting the search.").
    if (typeof backendDetail === 'string' && backendDetail.length < 200) return backendDetail;
    return 'We can\u2019t start the search right now. Please review your Master CV and try again.';
  }
  if (status === 404) {
    return 'A required service is unavailable (endpoint not found). Please refresh and try again.';
  }
  // 5xx, network, anything else: never surface raw backend detail.
  if (stage === 'preferences') return 'Could not save preferences. Please try again.';
  if (stage === 'canonical_cv') return 'Could not save your Master CV. Please try again.';
  return 'Something went wrong while starting the search. Please try again.';
}

  const handleConfirmAndStart = async () => {
    if (strongestEvidenceKeys.length === 1) {
      setSubmitState('error');
      setSubmitError('Choose two or three strongest evidence items, or leave this blank for now.');
      return;
    }
    if (!hasUsableText) {
      setSubmitState('error');
      setSubmitError(`Please add at least ${charsRemaining} more characters across the sections (especially Experience and Education) before starting the search.`);
      return;
    }
    if (preferencesDirty) {
      if (preferenceAutosaveTimerRef.current) window.clearTimeout(preferenceAutosaveTimerRef.current);
      const ok = await savePreferences();
      if (!ok) {
        return;
      }
    }
    setSubmitState('saving');
    setSubmitError('');
    try {
      try {
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        const saveResult = await saveCanonicalCv(submissionId, {
          canonical_cv_json: canonicalCvJson,
          canonical_cv_text: canonicalCvText,
          cv_review_status: 'reviewed',
          save_mode: 'explicit',
          expected_updated_at: serverUpdatedAtRef.current,
        });
        serverUpdatedAtRef.current = saveResult.updated_at || serverUpdatedAtRef.current;
        setAutosaveState('saved');
        setConflictMessage('');
      } catch (cvErr) {
        setSubmitState('error');
        if (cvErr.status === 409) {
          setAutosaveState('conflict');
          setConflictMessage('This Master CV was updated elsewhere. Review or force overwrite before starting the search.');
        }
        setSubmitError(describeStartSearchError(cvErr, { stage: 'canonical_cv' }));
        return;
      }
      setSavedAt(new Date());
      setSubmitState('starting');
      const result = onStartExactJob
        ? await onStartExactJob({ submissionId })
        : onConfirmMasterProfile
          ? await onConfirmMasterProfile({ submissionId, expectedUpdatedAt: serverUpdatedAtRef.current })
          : await confirmCvAndStartSearch(submissionId);
      if (onStartExactJob && result && result.success === false) {
        setSubmitState('error');
        setSubmitError(getSafeErrorMessage(result, { context: 'find-jobs-submit' }) ||
          'Could not complete this job fit check. Please try again.');
        return;
      }
      if (result && result.success === false && result.code === 'search_limit_reached') {
        setSubmitState('error');
        setSubmitError(getSafeErrorMessage(result, { context: 'find-jobs-submit' }) ||
          'You already have a recent search. Please wait before starting another one.');
        return;
      }
      onConfirmed?.(result);
    } catch (err) {
      setSubmitState('error');
      setSubmitError(describeStartSearchError(err, { stage: 'confirm' }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="master-cv-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading your CV…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="master-cv-load-error">
        <div className="border border-red-200 dark:border-red-800 rounded-lg p-6 bg-red-50/30 dark:bg-red-950/20 text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-3 text-red-500" />
          <p className="text-sm text-red-700 dark:text-red-300">{safeCvReviewText(loadError)}</p>
          {onBackToForm && (
            <Button variant="outline" className="mt-4" onClick={onBackToForm} data-testid="master-cv-back-to-form-btn">
              Return to Add CV
            </Button>
          )}
        </div>
      </div>
    );
  }

  const isSaving = submitState === 'saving';
  const isStarting = submitState === 'starting';
  const contactStatus = CONTACT_FIELDS.some(({ key }) => safeFormText(contact[key])) ? 'Complete' : 'Empty';
  const experienceStatus = experienceSectionStatus(experienceEntries);
  const educationStatus = filterNonEmptyEntries(educationEntries).length ? 'Complete' : 'Needs review';
  const projectStatus = projectEntries.map(projectEntryToWire).some((entry) => Object.values(entry).some((value) => Array.isArray(value) ? value.length : value)) ? 'Complete' : 'Empty';
  const skillsStatus = safeFormText(sections.skills) ? 'Complete' : (suggestedSearchSkills.length ? 'Needs review' : 'Empty');
  const defaultAccordionSections = [
    contactStatus !== 'Complete' && 'contact',
    !safeFormText(sections.professional_summary) && 'summary',
    experienceStatus !== 'Complete' && 'experience',
    educationStatus !== 'Complete' && 'education',
    skillsStatus !== 'Complete' && 'skills',
  ].filter(Boolean);
  const confirmedEvidence = confirmedExtractorItems(extractorReview, extractorDecisions);
  const selectedStrongestEvidence = confirmedEvidence.filter((item) => strongestEvidenceKeys.includes(extractorKey(item)));
  const snapshotSignature = candidateSnapshotSignature(candidateIntakeDraft, preferences, strongestEvidenceKeys);
  const snapshotSections = buildCandidateSnapshotSections(candidateIntakeDraft, preferences, selectedStrongestEvidence);
  const snapshotSectionSignatures = snapshotSections.reduce((acc, section) => {
    acc[section.key] = JSON.stringify(section.lines);
    return acc;
  }, {});
  const snapshotApprovalStale = Object.entries(snapshotApprovals || {}).some(([key, approval]) => {
    return Boolean(approval?.signature && approval.signature !== snapshotSectionSignatures[key]);
  });
  latestCandidateSnapshotStateRef.current = {
    strongestEvidenceKeys,
    approvals: snapshotApprovals,
    sourceSignature: snapshotSignature,
  };

  const persistCandidateSnapshotState = (nextStrongestEvidenceKeys, nextApprovals) => {
    const nextState = {
      strongestEvidenceKeys: uniqueArray(nextStrongestEvidenceKeys || []),
      approvals: nextApprovals && typeof nextApprovals === 'object' ? nextApprovals : {},
      sourceSignature: snapshotSignature,
    };
    latestCandidateSnapshotStateRef.current = nextState;
    writeBrowserDraft(userId, submissionId, latestFormRef.current.canonicalCvJson, serverUpdatedAtRef.current, preferences, nextState);
  };

  const toggleStrongestEvidence = (key) => {
    const current = uniqueArray(strongestEvidenceKeys);
    const next = current.includes(key) ? current.filter((item) => item !== key) : (current.length >= 3 ? current : [...current, key]);
    setStrongestEvidenceKeys(next);
    persistCandidateSnapshotState(next, snapshotApprovals);
  };

  const approveCandidateSnapshotSection = (sectionKey) => {
    const signature = snapshotSectionSignatures[sectionKey] || '';
    const next = {
      ...snapshotApprovals,
      [sectionKey]: {
        approved: true,
        approvedAt: new Date().toISOString(),
        signature,
      },
    };
    setSnapshotApprovals(next);
    persistCandidateSnapshotState(strongestEvidenceKeys, next);
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-8 text-[#22362f] md:px-10 md:py-12" data-testid="master-cv-review-page">
      <OnboardingCard className="mb-6 rounded-[2rem] p-5 md:p-6" data-testid="master-cv-workspace-header">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <span className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#d9c8aa] bg-white/45 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6c5d48]" data-testid="master-cv-eyebrow">
              {isProfileOnly ? 'Master Profile review' : 'Career Agent review'}
            </span>
            <h1 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight mb-3 text-[#172820]" data-testid="master-cv-title">
              {isProfileOnly ? 'Review your Master Profile' : 'Review your Master CV before search'}
            </h1>
            <p className="text-muted-foreground text-sm md:text-base max-w-2xl">
              {isProfileOnly ? 'Review and correct your extracted profile evidence, then confirm your profile.' : 'Your confirmed Master CV will guide matching and tailored documents. Review what matters now, then start the search.'}
            </p>
          </div>
          <SectionStatus status={hasUsableText ? 'Ready to confirm' : 'Needs review'} testid="master-cv-readiness-status" />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs" data-testid="master-cv-autosave-status">
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 ${autosaveState === 'conflict' ? 'border-amber-300 text-amber-800 bg-amber-50 dark:border-amber-700 dark:text-amber-200 dark:bg-amber-950/30' : 'border-border text-muted-foreground bg-card/50'}`} data-testid="master-cv-autosave-state">
            {autosaveState === 'saving' || autosaveState === 'retrying' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
            {autosaveState === 'dirty' && 'Saving…'}
            {autosaveState === 'saving' && 'Saving…'}
            {autosaveState === 'retrying' && 'Reconnecting'}
            {autosaveState === 'saved' && 'Saved'}
            {autosaveState === 'clean' && 'Saved'}
            {autosaveState === 'locally_saved' && 'Saved locally'}
            {autosaveState === 'conflict' && 'Conflict detected'}
            {autosaveState === 'error' && 'Autosave failed'}
          </span>
          {savedAt && <span className="text-muted-foreground" data-testid="master-cv-last-saved-at">Last saved {savedAt.toLocaleTimeString()}</span>}
        </div>
        <div className="mt-4"><OnboardingMilestones active="Review profile" /></div>
        <ConfirmationCard
          title="Your extracted CV evidence"
          primaryLabel="Review evidence"
          onConfirm={() => document.querySelector('[data-testid="master-cv-section-professional_summary"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          onEdit={() => document.querySelector('[data-testid="master-cv-section-skills"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          Check the evidence below, then correct any detail before confirming your Master CV.
        </ConfirmationCard>
        {resumeNotice && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200" data-testid="master-cv-resume-notice">
            {safeCvReviewText(resumeNotice)}
          </div>
        )}
        {conflictMessage && (
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100" data-testid="master-cv-conflict-alert">
            <span>{safeCvReviewText(conflictMessage)}</span>
            <Button type="button" variant="outline" size="sm" onClick={handleForceOverwrite} disabled={isSaving || isStarting} data-testid="master-cv-force-overwrite-button">
              Force overwrite
            </Button>
          </div>
        )}
      </OnboardingCard>

      <CareerAgentReadyCard
        preferences={preferences}
        canonicalCvJson={canonicalCvJson}
        extractorItemsCount={extractorReview ? extractorItems(extractorReview).length : 0}
      />

      {/* Search direction summary */}
      <section
        className="mb-6 rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/70 p-5"
        data-testid="draft-preferences-card"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between mb-1">
          <h2 className="font-serif-display text-xl font-medium tracking-tight">Search direction</h2>
          {prefSaveState === 'saved' && !preferencesDirty && (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300" data-testid="draft-preferences-saved">
              <CheckCircle className="w-3.5 h-3.5" /> Saved
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Roles, markets, level, skills, and matching details carried forward from your Career Agent setup and search details. Your Master CV fields below are separate and should reflect your CV evidence.
        </p>

        {!isPreferenceEditing ? (
          <>
            <PreferenceSummary preferences={preferences} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsPreferenceEditing(true)}
              disabled={prefSearchAlreadyStarted}
              className="mt-4"
              data-testid="draft-preferences-edit-btn"
            >
              Edit direction
            </Button>
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="draft-preferences-edit-grid">
            {PREFERENCE_FIELDS.map(({ key, label, placeholder, helper, type, testid }) => (
              <div key={key} className={key === 'target_roles' ? 'sm:col-span-2' : ''}>
                <Label htmlFor={`pref-${key}`} className="text-xs">{label}</Label>
                <Input
                  id={`pref-${key}`}
                  type={type === 'number' ? 'number' : 'text'}
                  value={preferences[key]}
                  onChange={(e) => handlePreferenceChange(key, e.target.value)}
                  placeholder={placeholder}
                  disabled={prefSearchAlreadyStarted}
                  className={WARM_INPUT_CLASS}
                  data-testid={testid || `draft-preferences-${key.replace(/_/g, '-')}`}
                />
                {helper && (
                  <p className="mt-1 text-[11px] text-muted-foreground">{helper}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {prefError && (
          <div
            className="mt-3 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 bg-red-50/30 dark:bg-red-950/20 text-xs text-red-700 dark:text-red-300"
            data-testid="draft-preferences-error"
          >
            {safeCvReviewText(prefError)}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={savePreferences}
            disabled={!preferencesDirty || prefSaveState === 'saving' || prefSearchAlreadyStarted}
            data-testid="draft-preferences-save-btn"
          >
            {prefSaveState === 'saving'
              ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>)
              : (<><Save className="w-3.5 h-3.5 mr-1.5" /> Save preferences</>)}
          </Button>
          {isPreferenceEditing && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsPreferenceEditing(false)}
              data-testid="draft-preferences-collapse-btn"
            >
              Collapse
            </Button>
          )}
          {preferencesDirty && prefSaveState !== 'saving' && !prefError && (
            <span className="text-[11px] text-muted-foreground">Unsaved changes</span>
          )}
        </div>
      </section>

      {/* Non-blocking parser warning - shown when most major sections are empty.
          Always reuses the friendly beta copy above; this banner is just a
          coloured nudge so the user can spot it after scrolling. */}
      {showParserWarning && (
        <div
          className="mb-6 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 bg-amber-50/60 dark:bg-amber-950/20 flex items-start gap-2.5 text-xs"
          data-testid="master-cv-parser-warning"
          role="status"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-amber-800 dark:text-amber-200 leading-relaxed">
            Please review and complete your Master CV below. We will use this confirmed version
            to match jobs and generate tailored CVs.
          </span>
        </div>
      )}

      {extractorReview && (
        <section className="mb-8 border border-[#d8c8ab] bg-[#fff9ee]/70 rounded-2xl p-5" data-testid="cv-extractor-review-section">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div>
              <h2 className="font-serif-display text-xl font-medium tracking-tight text-[#172820]">Review what your CV shows</h2>
              <p className="text-sm text-muted-foreground mt-1">
                We found possible evidence in your CV. Confirm only what looks right, edit anything unclear, or ignore what you do not want to use.
              </p>
            </div>
            <span className="rounded-full border border-[#d7c5a7] bg-white/60 px-3 py-1.5 text-xs text-[#30473e]" data-testid="cv-extractor-ai-status">
              {candidateAiStatus(extractorReview.ai_status)}
            </span>
          </div>
          {(extractorReview.missing_evidence_warnings || []).length > 0 && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-800" data-testid="cv-extractor-warnings">
              {(extractorReview.missing_evidence_warnings || []).map((warning) => <p key={warning}>{safeCvReviewText(warning)}</p>)}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {extractorItems(extractorReview).map((item) => {
              const key = extractorKey(item);
              const decision = extractorDecisions[key] || 'pending';
              const status = candidateEvidenceStatus(item.confidence, decision);
              if (decision === 'remove') return null;
              return (
                <div key={key} className="rounded-xl border border-[#d8c8ab] bg-white/55 p-4" data-testid={`cv-extractor-card-${key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#7b694d]">{safeCvReviewText(item.section)}</p>
                      <p className="text-sm font-medium text-[#22362f] mt-1">{safeCvReviewText(item.value)}</p>
                    </div>
                    <SectionStatus status={status} testid="cv-extractor-evidence-status" />
                  </div>
                  {item.evidence && (
                    <details className="mb-3 text-xs text-muted-foreground" data-testid="cv-extractor-evidence">
                      <summary className="cursor-pointer">View source evidence</summary>
                      <p className="mt-2 whitespace-pre-wrap">{safeCvReviewText(item.evidence)}</p>
                    </details>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {['confirm', 'edit', 'ignore', 'remove'].map((action) => (
                      <button
                        key={action}
                        type="button"
                        onClick={() => setExtractorDecisions((prev) => ({ ...prev, [key]: action }))}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${decision === action ? 'border-[#173d35] bg-[#173d35] text-[#fff8ed]' : 'border-[#d7c5a7] bg-white/60 text-[#30473e] hover:bg-white'}`}
                        data-testid={`cv-extractor-${action}-btn`}
                      >
                        {action === 'confirm' ? 'Use this' : action === 'edit' ? 'Edit' : action === 'remove' ? 'Remove' : 'Ignore'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {!dismissedSearchSkills && suggestedSearchSkills.length > 0 && (
        <section className="mb-8 rounded-2xl border border-[#d8c8ab] bg-white/55 p-5" data-testid="search-skills-suggestion-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="font-serif-display text-xl font-medium tracking-tight text-[#172820]">Skills you entered</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                These came from your search details, not directly from the CV. Add them only if they belong in your Master CV.
              </p>
            </div>
            <button type="button" onClick={() => setDismissedSearchSkills(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="dismiss-search-skills-suggestion">
              Dismiss
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="search-skills-suggestion-chips">
            {suggestedSearchSkills.map((skill) => <span key={skill} className="rounded-full border border-[#d7c5a7] bg-[#fff9ee] px-3 py-1.5 text-sm text-[#30473e]">{safeCvReviewText(skill)}</span>)}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addSearchSkillsToMasterCv} className="mt-4" data-testid="add-search-skills-to-master-cv-btn">
            Add these to Master CV skills
          </Button>
        </section>
      )}

      <section className="mb-6 rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-5" data-testid="candidate-snapshot-preview">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-[#7b694d]">Private snapshot preview</p>
            <h2 className="font-serif-display text-2xl font-medium tracking-tight text-[#172820]">Candidate Snapshot</h2>
            <p className="mt-1 text-sm leading-relaxed text-[#6f6657]">Your Snapshot is a short summary built only from answers you gave and evidence you approved. It never guesses. You decide if it is ever shown to anyone, and you can edit or delete it at any time. Local preview only in this phase.</p>
          </div>
          <span className={`rounded-full px-3 py-1.5 text-xs font-medium ${snapshotSections.length && !snapshotApprovalStale && snapshotSections.every((section) => snapshotApprovals[section.key]?.signature === snapshotSectionSignatures[section.key]) ? 'bg-[#173d35] text-[#fff8ed]' : 'bg-[#fff4dc] text-[#6f5b2f]'}`}>{snapshotSections.length && !snapshotApprovalStale && snapshotSections.every((section) => snapshotApprovals[section.key]?.signature === snapshotSectionSignatures[section.key]) ? 'Sections approved locally' : 'Needs section approval'}</span>
        </div>
        {confirmedEvidence.length ? (
          <div className="mb-4 space-y-2" data-testid="strongest-evidence-selection">
            <p className="text-sm font-medium text-[#22362f]">Which of these best show what you can offer? Pick two or three.</p>
            <p className="text-sm text-[#6f6657]">Your agent will lead with these in documents and summaries.</p>
            <div className="flex flex-wrap gap-2">
              {confirmedEvidence.map((item) => {
                const key = extractorKey(item);
                const selected = strongestEvidenceKeys.includes(key);
                return (
                  <button key={key} type="button" aria-pressed={selected} onClick={() => toggleStrongestEvidence(key)} className={`inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3 py-2 text-sm ${selected ? 'border-[#173d35] bg-[#173d35] text-[#fff8ed]' : 'border-[#8b7652] bg-white/55 text-[#30473e]'}`} data-testid={`strongest-evidence-${key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    {selected && <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />}{item.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : <p className="mb-4 text-sm text-[#6f6657]" data-testid="strongest-evidence-empty">Confirm CV evidence first, then choose the strongest evidence for a future snapshot.</p>}
        {strongestEvidenceKeys.length === 1 && <p className="mb-4 text-sm text-[#6f5b2f]" data-testid="strongest-evidence-incomplete">Choose one more evidence card, or deselect this one to skip for now.</p>}
        <p className="mb-4 text-sm leading-relaxed text-[#6f6657]" data-testid="candidate-snapshot-privacy-note">
          Preview exactly what may later become recruiter-facing. Recruiter-facing output is disabled in this phase. Motivations and ways-of-working answers remain private unless you separately approve recruiter-facing use. Things you choose to avoid always remain private and are never included in recruiter summaries or application documents.
        </p>
        <div className="space-y-3" data-testid="candidate-snapshot-lines">
          {snapshotSections.length ? snapshotSections.map((section) => {
            const approved = snapshotApprovals[section.key]?.signature === snapshotSectionSignatures[section.key];
            const stale = Boolean(snapshotApprovals[section.key]?.signature && !approved);
            return (
              <div key={section.key} className="rounded-xl border border-[#d8c8ab] bg-white/55 p-4" data-testid={`candidate-snapshot-section-${section.key}`}>
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-sm font-medium text-[#22362f]">{section.title}</h3>
                  <span className={`rounded-full px-3 py-1 text-xs ${approved ? 'bg-[#173d35] text-[#fff8ed]' : 'bg-[#fff4dc] text-[#6f5b2f]'}`}>{approved ? 'Approved locally' : stale ? 'Needs review' : 'Not approved'}</span>
                </div>
                {section.lines.map((line) => <p key={line} className="text-sm leading-relaxed text-[#30473e]">{line}</p>)}
                <Button type="button" className="btn-secondary mt-4" onClick={() => approveCandidateSnapshotSection(section.key)} data-testid={`approve-candidate-snapshot-${section.key}`}>Approve this section</Button>
              </div>
            );
          }) : <p className="rounded-xl border border-[#d8c8ab] bg-white/55 p-4 text-sm text-[#6f6657]">No snapshot lines yet. Confirm evidence or add answers first.</p>}
        </div>
        {snapshotApprovalStale && <p className="mt-3 text-sm text-[#6f5b2f]" data-testid="candidate-snapshot-stale">Snapshot approval needs review because source answers changed.</p>}
      </section>

      <section className="mb-8 rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/70 p-5" data-testid="master-cv-accordion-workspace">
        <div className="mb-4">
          <h2 className="font-serif-display text-2xl font-medium tracking-tight text-[#172820]">Master CV workspace</h2>
          <p className="mt-1 text-sm text-muted-foreground">Open the sections that need attention. Filled sections stay collapsed until you want to edit them.</p>
        </div>
        <Accordion type="multiple" defaultValue={defaultAccordionSections} className="space-y-3">
          <AccordionItem value="contact" className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid="master-cv-accordion-contact">
            <AccordionTrigger data-testid="master-cv-accordion-contact-trigger">
              <span className="flex w-full items-center justify-between gap-3 pr-3"><span>Contact</span><SectionStatus status={contactStatus} testid="master-cv-contact-status" /></span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="master-cv-contact">
          {CONTACT_FIELDS.map(({ key, label, placeholder }) => (
            <div key={key}>
              <Label htmlFor={`contact-${key}`} className="text-xs">{label}</Label>
              <Input
                id={`contact-${key}`}
                value={contact[key]}
                onChange={(e) => handleContactChange(key, key === 'phone' ? cleanPhoneText(e.target.value) : e.target.value)}
                placeholder={placeholder}
                  className={WARM_INPUT_CLASS}
                data-testid={`master-cv-contact-${key}`}
              />
            </div>
          ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {SECTIONS.filter((s) => s.key === 'professional_summary').map(({ key, label, helper, rows }) => (
            <AccordionItem key={key} value="summary" className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid={`master-cv-section-${key}`}>
              <AccordionTrigger data-testid="master-cv-accordion-summary-trigger">
                <span className="flex w-full items-center justify-between gap-3 pr-3"><span>{label}</span><SectionStatus status={sectionStatusFromContent(sections[key])} testid="master-cv-summary-status" /></span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-xs text-muted-foreground mb-2">{helper}</p>
                <Textarea rows={rows} value={sections[key]} onChange={(e) => handleSectionChange(key, e.target.value)} className={WARM_INPUT_CLASS} data-testid={`master-cv-textarea-${key}`} />
                {!safeFormText(sections[key]) && <p className="mt-2 text-xs text-muted-foreground">Add a short summary if you want one. We will not invent it.</p>}
              </AccordionContent>
            </AccordionItem>
          ))}

          <AccordionItem value="experience" className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid="master-cv-section-experience">
            <AccordionTrigger data-testid="master-cv-accordion-experience-trigger">
              <span className="flex w-full items-center justify-between gap-3 pr-3"><span>Experience</span><SectionStatus status={experienceStatus} testid="master-cv-experience-status" /></span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="mb-3 text-xs text-muted-foreground">Add one card per role. Roles you do not fill in are skipped on save.</p>
              {experienceStatus !== 'Complete' && <p className="mb-3 text-xs text-[#6f5b2f]">Experience is complete only when Position, Company and Dates are present. Partial records are preserved for your review.</p>}
              <div className="space-y-3">
                {experienceEntries.map((entry, index) => {
                  const missingFields = missingExperienceFields(entry);
                  return (
                    <div
                      key={`exp-${index}`}
                      className={`rounded-lg border bg-white/55 p-4 ${missingFields.length ? 'border-[#b8842f]' : 'border-[#d8c8ab]'}`}
                      data-testid={`master-cv-experience-card-${index}`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Experience #{index + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExperienceEntry(index)}
                          className="h-7 px-2 text-muted-foreground hover:text-red-600"
                          data-testid={`master-cv-experience-remove-${index}`}
                          aria-label={`Remove experience ${index + 1}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {EXPERIENCE_FIELDS.map(({ key, label, placeholder }) => (
                          <div key={key}>
                            <Label htmlFor={`exp-${index}-${key}`} className="text-xs">{label}</Label>
                            <Input
                              id={`exp-${index}-${key}`}
                              value={entry[key]}
                              onChange={(e) => updateExperienceField(index, key, e.target.value)}
                              placeholder={placeholder}
                              className={`${WARM_INPUT_CLASS} ${missingFields.includes(key) ? 'border-[#b8842f]' : ''}`}
                              data-testid={`master-cv-experience-${key}-${index}`}
                            />
                          </div>
                        ))}
                       </div>
                       {missingFields.length > 0 && <p className="mt-3 text-xs text-[#6f5b2f]" data-testid={`master-cv-experience-${index}-missing-fields`}>Missing mandatory fields: {missingFields.map((key) => EXPERIENCE_REQUIRED_LABELS[key]).join(', ')}</p>}
                       {entry.unresolved_evidence && <p className="mt-3 text-xs text-[#6f5b2f]" data-testid={`master-cv-experience-${index}-unresolved-evidence`}>This extracted entry was kept as raw evidence. Review or edit the details before saving.</p>}
                       <div className="mt-3">
                        <Label htmlFor={`exp-${index}-details`} className="text-xs">Details / Responsibilities / Achievements</Label>
                        <Textarea
                          id={`exp-${index}-details`}
                          rows={4}
                          value={entry.details}
                          onChange={(e) => updateExperienceField(index, 'details', e.target.value)}
                          placeholder="What did you do? Key wins, technologies, scope"
                          className={WARM_INPUT_CLASS}
                          data-testid={`master-cv-experience-details-${index}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addExperienceEntry}
                className="mt-3"
                data-testid="master-cv-experience-add"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add another experience
              </Button>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="education" className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid="master-cv-section-education">
            <AccordionTrigger data-testid="master-cv-accordion-education-trigger">
              <span className="flex w-full items-center justify-between gap-3 pr-3"><span>Education</span><SectionStatus status={educationStatus} testid="master-cv-education-status" /></span>
            </AccordionTrigger>
            <AccordionContent>
        <p className="text-xs text-muted-foreground mb-3">
          Degrees, schools, fields, dates. Extraction often misses this. Please double-check.
        </p>
        <div className="space-y-3">
          {educationEntries.map((entry, index) => (
            <div
              key={`edu-${index}`}
              className="rounded-xl border border-[#d8c8ab] bg-white/55 p-4"
              data-testid={`master-cv-education-card-${index}`}
            >
               <div className="flex items-center justify-between mb-3">
                 <span className="text-xs font-medium text-muted-foreground">Education #{index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEducationEntry(index)}
                  className="h-7 px-2 text-muted-foreground hover:text-red-600"
                  data-testid={`master-cv-education-remove-${index}`}
                  aria-label={`Remove education ${index + 1}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                 </Button>
               </div>
               {entry.unresolved_evidence && <p className="mb-3 text-xs text-[#6f5b2f]" data-testid={`master-cv-education-${index}-unresolved-evidence`}>This extracted entry was kept as raw evidence. Review or edit the details before saving.</p>}
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EDUCATION_FIELDS.map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <Label htmlFor={`edu-${index}-${key}`} className="text-xs">{label}</Label>
                    <Input
                      id={`edu-${index}-${key}`}
                      value={entry[key]}
                      onChange={(e) => updateEducationField(index, key, e.target.value)}
                      placeholder={placeholder}
                      className={WARM_INPUT_CLASS}
                      data-testid={`master-cv-education-${key}-${index}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Label htmlFor={`edu-${index}-details`} className="text-xs">Details</Label>
                <Textarea
                  id={`edu-${index}-details`}
                  rows={3}
                  value={entry.details}
                  onChange={(e) => updateEducationField(index, 'details', e.target.value)}
                  placeholder="Honours, thesis, GPA, key coursework…"
                  className={WARM_INPUT_CLASS}
                  data-testid={`master-cv-education-details-${index}`}
                />
              </div>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addEducationEntry}
          className="mt-3"
          data-testid="master-cv-education-add"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add another education
        </Button>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="projects" className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid="master-cv-section-projects">
            <AccordionTrigger data-testid="master-cv-accordion-projects-trigger">
              <span className="flex w-full items-center justify-between gap-3 pr-3"><span>Projects</span><SectionStatus status={projectStatus} testid="master-cv-projects-status" /></span>
            </AccordionTrigger>
            <AccordionContent>
        <p className="text-xs text-muted-foreground mb-2">Keep project context and bullet lines separate.</p>
        <div className="space-y-4">
          {projectEntries.map((entry, index) => (
            <div key={`project-${index}`} className="rounded-xl border border-[#d8c8ab] bg-white/55 p-4" data-testid={`master-cv-project-${index}`}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs font-medium text-muted-foreground">Project {index + 1}</span>
                {projectEntries.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeProjectEntry(index)} data-testid={`master-cv-project-remove-${index}`}>
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ['title', 'Project title'],
                  ['role', 'Role / context'],
                  ['tools', 'Tools / technologies'],
                  ['dates', 'Dates / duration'],
                ].map(([field, label]) => (
                  <div key={field}>
                    <Label htmlFor={`project-${index}-${field}`} className="text-xs">{label}</Label>
                    <Input
                      id={`project-${index}-${field}`}
                      value={entry[field] || ''}
                      onChange={(e) => updateProjectField(index, field, e.target.value)}
                      className={WARM_INPUT_CLASS}
                      data-testid={`master-cv-project-${field}-${index}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Label htmlFor={`project-${index}-description`} className="text-xs">Description</Label>
                <Textarea
                  id={`project-${index}-description`}
                  rows={4}
                  value={entry.description || ''}
                  onChange={(e) => updateProjectField(index, 'description', e.target.value)}
                  className={WARM_INPUT_CLASS}
                  data-testid={`master-cv-project-description-${index}`}
                />
              </div>
              <div className="mt-3">
                <Label htmlFor={`project-${index}-bullets`} className="text-xs">Project lines / bullet points</Label>
                <Textarea
                  id={`project-${index}-bullets`}
                  rows={5}
                  value={entry.bullets || ''}
                  onChange={(e) => updateProjectField(index, 'bullets', e.target.value)}
                  placeholder="One bullet per line"
                  className={WARM_INPUT_CLASS}
                  data-testid={`master-cv-project-bullets-${index}`}
                />
              </div>
              <div className="mt-3">
                <Label htmlFor={`project-${index}-links`} className="text-xs">Links</Label>
                <Textarea
                  id={`project-${index}-links`}
                  rows={2}
                  value={entry.links || ''}
                  onChange={(e) => updateProjectField(index, 'links', e.target.value)}
                  placeholder="One link per line"
                  className={WARM_INPUT_CLASS}
                  data-testid={`master-cv-project-links-${index}`}
                />
              </div>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addProjectEntry} className="mt-3" data-testid="master-cv-project-add">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add another project
        </Button>
            </AccordionContent>
          </AccordionItem>

          {SECTIONS.filter((s) => s.key !== 'professional_summary' && s.key !== 'projects' && s.key !== 'raw_notes').map(({ key, label, helper, rows }) => (
            <AccordionItem key={key} value={key} className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid={`master-cv-section-${key}`}>
              <AccordionTrigger data-testid={`master-cv-accordion-${key}-trigger`}>
                <span className="flex w-full items-center justify-between gap-3 pr-3"><span>{label}</span><SectionStatus status={key === 'skills' ? skillsStatus : sectionStatusFromContent(sections[key])} testid={`master-cv-${key}-status`} /></span>
              </AccordionTrigger>
              <AccordionContent>
          <p className="text-xs text-muted-foreground mb-2">{helper}</p>
          {key === 'skills' && !safeFormText(sections[key]) && <p className="mb-2 text-xs text-muted-foreground">No confirmed skills yet. Add skills or use suggestions from your CV and search details.</p>}
          {key === 'skills' ? <TagEditor values={splitList(sections.skills)} onChange={(values) => handleSectionChange('skills', values.join('\n'))} /> : <Textarea
            rows={rows}
            value={sections[key]}
            onChange={(e) => handleSectionChange(key, e.target.value)}
            className={WARM_INPUT_CLASS}
            data-testid={`master-cv-textarea-${key}`}
          />}
              </AccordionContent>
            </AccordionItem>
          ))}

          {!isProfileOnly && rawCvText && (
            <AccordionItem value="raw-text" className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid="master-cv-raw-extract">
              <AccordionTrigger data-testid="master-cv-accordion-raw-text-trigger">
                <span className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Raw extracted text</span>
              </AccordionTrigger>
              <AccordionContent>
                <pre className="text-xs whitespace-pre-wrap text-muted-foreground max-h-72 overflow-auto">{safeFormText(rawCvText)}</pre>
              </AccordionContent>
            </AccordionItem>
          )}

          {!isProfileOnly && SECTIONS.filter((s) => s.key === 'raw_notes').map(({ key, label, helper, rows }) => (
            <AccordionItem key={key} value={key} className="rounded-xl border border-[#d8c8ab] bg-white/55 px-4" data-testid={`master-cv-section-${key}`}>
              <AccordionTrigger data-testid="master-cv-accordion-raw-notes-trigger">
                <span className="flex w-full items-center justify-between gap-3 pr-3"><span>{label}</span><SectionStatus status={sectionStatusFromContent(sections[key])} testid="master-cv-raw-notes-status" /></span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-xs text-muted-foreground mb-2">{helper}</p>
                <Textarea rows={rows} value={sections[key]} onChange={(e) => handleSectionChange(key, e.target.value)} className={WARM_INPUT_CLASS} data-testid={`master-cv-textarea-${key}`} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Save status / errors */}
      {submitError && (
        <div className="mb-4 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2.5 bg-red-50/30 dark:bg-red-950/20 text-xs text-red-700 dark:text-red-300" data-testid="master-cv-error">
          {safeCvReviewText(submitError)}
        </div>
      )}
      {savedAt && submitState === 'idle' && (
        <div className="mb-4 inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300" data-testid="master-cv-saved">
          <CheckCircle className="w-3.5 h-3.5" /> Saved {savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}

      {/* Content length hint */}
      {!hasUsableText && (
        <p className="mb-3 text-xs text-muted-foreground" data-testid="master-cv-min-hint">
          Add roughly {charsRemaining} more characters of content before {isProfileOnly ? 'confirming your profile' : 'starting the search'}
          ({trimmedLen} / {MIN_CANONICAL_CHARS}).
        </p>
      )}

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-border">
        {onBackToForm ? (
          <Button
            variant="outline"
            onClick={handleBackToForm}
            disabled={isSaving || isStarting}
            data-testid="master-cv-back-to-form-btn"
          >
            {isProfileOnly ? 'Back to upload' : 'Back to edit preferences'}
          </Button>
        ) : <div />}
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={handleSaveDraft}
            disabled={isSaving || isStarting}
            data-testid="master-cv-save-btn"
          >
            {isSaving
              ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving…</>)
              : (<><Save className="w-3.5 h-3.5 mr-2" /> Save draft</>)}
          </Button>
          <Button
            className="btn-primary w-full sm:w-auto"
            onClick={handleConfirmAndStart}
            disabled={isSaving || isStarting}
            data-testid="master-cv-confirm-btn"
          >
            {isStarting
              ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> {isProfileOnly ? 'Confirming profile…' : 'Starting search…'}</>)
              : (isProfileOnly ? 'Confirm your profile' : 'Save and Start Search')}
          </Button>
        </div>
      </div>
    </div>
  );
}
